import { ReactComponent as DeleteImg } from "./assets/delete.svg";
import { ReactComponent as SendImg } from "./assets/send.svg";
import { ReactComponent as SmileyImg } from "./assets/smiley.svg";
import { ReactComponent as SettingsImg } from "./assets/settings.svg";
import { ReactComponent as SurveyImg } from "./assets/bar-chart.svg";
import { ReactComponent as AcceptImg } from "./assets/accept.svg";
import { ReactComponent as RejectImg } from "./assets/reject.svg";

import React, { useState, useRef, useMemo, useEffect } from "react";
import TextareaAutosize from 'react-textarea-autosize';
import moment from "moment";
import { Socket } from "phoenix";
import Picker from 'emoji-picker-react';
import AutoLinkText from 'react-autolink-text2';

import PollForm from "./polls/Form";
import PollManagement from "./polls/Management";
import PollVoting from "./polls/Voting";
import PollList from "./polls/List";

const MAX_HISTORY = 150;
const SHEET_SETTINGS = "settings";
const SHEET_POLL = "poll";

export default class Chat extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            userId: null,
            loaded: false,
            messages: [],
            readOnly: false,
            type: null,
            isDownloading: false,
            visibleSheet: null,
            username: null,
            poll: null,
            polls: [],
            pollsShowForm: false
        };

        this.socket = new Socket(`${process.env.REACT_APP_CHAT_URL.replace("http", "ws")}/socket`);
        this.channel = null;
        this._messagesRef = React.createRef();
        this._onMessage = this._onMessage.bind(this);
        this._renderMessage = this._renderMessage.bind(this);
        this._onJoin = this._onJoin.bind(this);
        this._onDeleteMessage = this._onDeleteMessage.bind(this);
        this._onClearMessages = this._onClearMessages.bind(this);
        this._onApproveMessage = this._onApproveMessage.bind(this);
        this._onRejectMessage = this._onRejectMessage.bind(this);
        this._onChangeType = this._onChangeType.bind(this)
        this._onChangeReadOnly = this._onChangeReadOnly.bind(this)
        this._downloadMessages = this._downloadMessages.bind(this)
        this._onPollStarted = this._onPollStarted.bind(this)
        this._onPollStopped = this._onPollStopped.bind(this)

        this.scrollToBottom = this.scrollToBottom.bind(this);
        this.setVisibleSheet = this.setVisibleSheet.bind(this);

        // polls
        this._startPoll = this._startPoll.bind(this);
        this._onPollVote = this._onPollVote.bind(this);
        this._onPollUpdated = this._onPollUpdated.bind(this);
        this._onPollRemoved = this._onPollRemoved.bind(this);
        this._onStopPoll = this._onStopPoll.bind(this);
        this._onRemovePoll = this._onRemovePoll.bind(this);
        this._onPollCreated = this._onPollCreated.bind(this);
        this._onPollDeleted = this._onPollDeleted.bind(this);
        this._savePoll = this._savePoll.bind(this);
        this._deletePoll = this._deletePoll.bind(this);
        this._sharePoll = this._sharePoll.bind(this);
    }

    componentDidMount() {
        this.socket.connect();

        const prefix = this.props.isAdmin ? "moderator" : "public";
        this.channel = this.socket.channel(`${prefix}:${this.props.room}`, { usertoken: this.props.token });
        this.channel.on("new_msg", this._onMessage);
        this.channel.on("poll_started", this._onPollStarted);
        this.channel.on("poll_stopped", this._onPollStopped);
        this.channel.on("poll_updated", this._onPollUpdated);
        this.channel.on("poll_removed", this._onPollRemoved);
        this.channel.on("poll_created", this._onPollCreated);
        this.channel.on("poll_deleted", this._onPollDeleted);
        this.channel.on("approved", this._onApproveMessage);
        this.channel.on("rejected", this._onRejectMessage);
        this.channel.on("read_only", this._onChangeReadOnly);
        this.channel.on("enqueue", this._onMessage);
        this.channel.on("type", this._onChangeType);
        this.channel.on("delete_message", this._onDeleteMessage);
        this.channel.on("clear_messages", this._onClearMessages);
        this.channel.join()
            .receive("ok", this._onJoin)
            .receive("error", console.error)
    }

    componentWillUnmount() {
        this.channel.leave()
        this.channel = null;

        this.socket.disconnect();
        this.socket = null;
    }

    // backward compatibility: Should be removed after Studio & VT dont have a settings button
    toggleSettings() {
        this.setState({
            visibleSheet: this.state.visibleSheet === SHEET_SETTINGS ? null : SHEET_SETTINGS
        });
    }

    setVisibleSheet(sheet) {
        this.setState({ visibleSheet: sheet === this.state.visibleSheet ? null : sheet }, this.scrollToBottom);
    }

    scrollToBottom() {
        if (this._messagesRef === null || this._messagesRef.current === null) return;
        this._messagesRef.current.scrollTo(0, this._messagesRef.current.scrollHeight)
    }

    setReadOnly(isReadOnly) {
        this.channel.push("read_only", isReadOnly)
            .receive("ok", () => this.setState({ readOnly: isReadOnly }))
            .receive("error", console.error);
    }

    setType(type) {
        this.channel.push("type", type)
            .receive("ok", () => this.setState({ type: type }))
            .receive("error", console.error);
    }

    clearMessages() {
        try {
            if (!window.confirm("Are you sure that you want to clear all messages?")) return;
        } catch (e) {
            console.error(e);
        }

        this.channel.push("clear_messages", {})
            .receive("ok", this._onClearMessages)
            .receive("error", console.error);
    }

    _onChangeReadOnly({ read_only, readOnly }) {
        // TODO: Remove `readOnly` in favor of `read_only`
        this.setState({ readOnly: !!read_only || !!readOnly });
    }

    _onChangeType({ type }) {
        this.setState({ type });
    }

    _downloadMessages() {
        this.setState({ isDownloading: true });

        fetch(`${process.env.REACT_APP_CHAT_URL}/api/v1/${this.props.room}/history`, {
            headers: { "Authorization": `Bearer ${this.props.token}` }
        })
            .then((response) => {
                if (response.ok && response.status === 200) {
                    return response.blob();
                } else {
                    throw response;
                }
            })
            .then((blob) => {
                const url = URL.createObjectURL(blob);

                // Create an anchor element to "point" to it
                const anchor = document.createElement('a');
                anchor.href = url;
                anchor.download = 'Chat-history.csv';
                anchor.click();

                // Discard the object data
                URL.revokeObjectURL(url);
            })
            .then(() => {
                this.setState({ isDownloading: false });
            })
            .catch((error) => {
                this.setState({ isDownloading: false });
                throw error;
            });
    }

    _onApproveMessage(message) {
        const idx = this.state.messages.findIndex((m) => m.id === message.id);

        if (idx > -1) {
            this.setState({
                messages: this.state.messages.map((m) => {
                    if (m.id === message.id) return message;
                    return m;
                })
            }, this.scrollToBottom);
        } else {
            this.setState({
                messages: this.state.messages.concat([message]).sort((a, b) => a.timestamp - b.timestamp).slice(-MAX_HISTORY)
            }, this.scrollToBottom);
        }
    }

    _onRejectMessage({ id }) {
        this.setState({
            messages: this.state.messages.filter(m => m.id !== id)
        }, this.scrollToBottom);
    }


    _deleteMessage(message) {
        try {
            if (!window.confirm("Are you sure that you want to delete this message?")) return;
        } catch (e) {
            console.error(e);
        }

        this.channel.push("delete_message", { id: message.id })
    }

    _onMessage(message) {
        this.setState({
            messages: this.state.messages.concat([message]).slice(-MAX_HISTORY)
        }, () => {
            this.scrollToBottom();

            if (typeof this.props.onIncomingMessage !== "undefined") {
                this.props.onIncomingMessage();
            }
        });
    }

    _onMessages(messages) {
        this.setState({
            loaded: true,
            messages: messages.sort((a, b) => a.timestamp - b.timestamp).slice(-MAX_HISTORY)
        }, () => {
            this.scrollToBottom()
        });
    }

    _onDeleteMessage({ id }) {
        this.setState({ messages: this.state.messages.filter(m => m.id !== id) });
    }

    _onClearMessages(_) {
        this.setState({ messages: [] });
    }

    // TODO: readOnly can be removed. It has been replaced with read_only.
    _onJoin({ user_id, messages, type, readOnly, read_only, poll, prepared_polls }) {
        this.setState({
            userId: user_id,
            type: type,
            poll: poll,
            readOnly: !!read_only || !!readOnly,
            polls: prepared_polls || [],
            loaded: false
        }, () => {
            this._setUsername(this.props.username || this.state.username);
            this._onMessages(messages);
        })
    }

    _setUsername(username) {
        if (username === null || username === "") return;

        this.channel.push("change_username", { username })
            .receive("ok", () => this.setState({ username }))
            .receive("error", console.error);
    }

    _handleError(error) {
        if (error.reason === "Not alive") {
            console.warn("not alive")
            this.channel.leave()
            this.channel.join()
                .receive("ok", this._onJoin)
                .receive("error", console.error);
        } else {
            console.error(error)
        }
    }
    _renderMessage(accumulator, message) {
        // TODO: `this.props.token === message.user_id` can be removed with the next update of the chat js.
        const isMe = this.props.token === message.user_id || this.state.userId === message.user_id || (this.props.isAdmin && message.moderator);
        const showDelete = (this.props.isAdmin && !message.pending) || isMe;
        const time = moment.unix(message.timestamp / 1000).format("HH:mm");
        const isServer = message.user_id === "[SERVER]"

        let newUserOrTime = true;
        const lastMessage = accumulator[accumulator.length - 1];
        if (lastMessage) {
            newUserOrTime = lastMessage.props["data-user-id"] !== message.user_id || lastMessage.props["data-time"] !== time;
        }

        const messageEl = (
            <div key={message.id} data-user-id={message.user_id} data-time={time} className={`select-none max-w-90 text-sm flex flex-col mb-1 last:mb-0 ${isMe ? "ml-auto items-end" : "items-start"} floating:items-end floating:ml-auto`}>
                <div className="flex items-center">
                    <div style={{ wordBreak: "break-word" }} className={`select-text group inline-block break-words relative rounded-2xl px-2.5 py-1.5 ${isMe ? `bg-primary bg-opacity-50 text-on-primary` : `bg-gray-300 text-black`} ${message.pending ? "bg-gradient-stripes" : ""}`}>
                        {isServer ? <span dangerouslySetInnerHTML={{ __html: message.text }}></span> : <AutoLinkText linkProps={{ target: "_blank", className: "underline" }} text={message.text} />}
                        {showDelete && <button onClick={() => this._deleteMessage(message)} className="rounded-full p-1 absolute opacity-0 group-hover:opacity-100 bg-red-500 text-white right-1 top-1">
                            <DeleteImg width="16" height="16" />
                        </button>}
                    </div>
                    {message.pending && this.props.isAdmin && this._renderActions(message)}
                </div >
                {newUserOrTime && <small className="px-2 text-gray-500">{isServer ? "System" : message.username} - {time}</small>}
            </div>
        );

        return [...accumulator, messageEl];
    }

    _renderActions(message) {
        return <>
            <div className="ml-2 cursor-pointer text-gray-500 hover:text-primary" onClick={this.channel.push.bind(this.channel, "approve", message)}>
                <AcceptImg />
            </div>
            <div className="ml-1 cursor-pointer text-gray-500 hover:text-primary" onClick={this.channel.push.bind(this.channel, "reject", message)}>
                <RejectImg />
            </div>
        </>;
    }

    _renderEmptyState() {
        return (
            <div className="center-vertically floating:invisible">
                <h2 className="text-2xl">There are no messages!</h2>
                <p>Start a new conversation</p>
            </div>
        );
    }

    _renderInput() {
        if (this.state.readOnly && !this.props.isAdmin) return null;

        if (this.props.username == null && this.state.username == null) {
            return <LoginForm onLogin={(name) => this._setUsername(name)} />;
        }

        return <ChatForm
            channel={this.channel}
            handleError={(error) => this._handleError(error)}
            showSettingsSheet={this.props.isAdmin ? () => this.setVisibleSheet(SHEET_SETTINGS) : null}
            showSurveySheet={this.props.isAdmin ? () => this.setVisibleSheet(SHEET_POLL) : null}
            visibleSheet={this.state.visibleSheet}
            onMessage={(message) => message.pending ? this._onMessage(message) : null} />
    }

    _renderBottomSheet() {
        if (!this.state.visibleSheet) return;

        let child;
        switch (this.state.visibleSheet) {
            case SHEET_SETTINGS:
                child = this._renderSettings();
                break;
            case SHEET_POLL:
                if (this.state.poll) {
                    child = <PollManagement
                        onStop={this.state.poll.active ? this._onStopPoll : null}
                        onRemove={!this.state.poll.active ? this._onRemovePoll : null}
                        onShare={this._sharePoll}>{this.state.poll}</PollManagement>;
                } else {
                    child = this._renderPolls();
                }
                break;
            default:
                throw new Error("Unknown sheet");
        }

        return <div className="bg-gray-50 p-4 text-sm border-t">{child}</div>;
    }

    _renderPolls() {
        if (this.state.polls.length === 0 || this.state.pollsShowForm) {
            return <PollForm
                onSubmit={this._startPoll}
                onSave={this._savePoll}
                onCancel={this.state.polls.length > 0 ? () => this.setState({ pollsShowForm: false }) : null} />;
        }

        return <>
            <PollList onStart={this._startPoll} onDelete={this._deletePoll}>{this.state.polls}</PollList>
            <button className="btn btn-secondary w-full mt-3" onClick={() => this.setState({ pollsShowForm: true })}>Create new poll</button>
        </>;
    }

    // Section: Polls

    _startPoll(type, question, answers) {
        return new Promise((resolve, reject) => {
            this.channel.push("start_poll", { type, question, answers })
                .receive("ok", () => {
                    this.setState({ visibleSheet: null, pollsShowForm: false });
                    resolve();
                })
                .receive("error", reject);
        });
    }

    _onPollStarted(details) {
        if (typeof this.props.onIncomingMessage !== "undefined") {
            this.props.onIncomingMessage();
        }

        this.setState({
            poll: {
                details,
                active: true,
                results: this.props.isAdmin ? details.answers.map((a) => 0) : null,
                choice: null
            }
        })
    }

    _onPollVote(choice) {
        return new Promise((resolve, reject) => {
            this.channel.push("vote", { choice })
                .receive("ok", ({ results }) => {
                    this.setState({
                        poll: { ...this.state.poll, choice, results }
                    }, resolve);
                })
                .receive("error", reject);
        });
    }

    _sharePoll() {
        return new Promise((resolve, reject) => {
            this.channel.push("share_results")
                .receive("ok", resolve)
                .receive("error", reject);
        });
    }

    _onStopPoll() {
        return new Promise((resolve, reject) => {
            this.channel.push("stop_poll")
                .receive("ok", () => {
                    this.setState({
                        poll: { ...this.state.poll, active: false }
                    }, resolve);
                })
                .receive("error", reject);
        });
    }

    _onPollUpdated({ results }) {
        console.log(results)
        if (!this.state.poll) return;

        this.setState({
            poll: { ...this.state.poll, results }
        })
    }

    _onPollStopped() {
        if (!this.state.poll) return;

        this.setState({
            poll: { ...this.state.poll, active: false }
        });
    }

    _onRemovePoll() {
        this.setState({ visibleSheet: null }, this.scrollToBottom);

        return new Promise((resolve, reject) => {
            this.channel.push("remove_poll")
                .receive("ok", () => {
                    this.setState({ poll: null }, resolve);
                })
                .receive("error", reject);
        });
    }

    _onPollRemoved() {
        this.setState({ poll: null }, this.scrollToBottom);
    }

    _savePoll(type, question, answers) {
        return new Promise((resolve, reject) => {
            this.channel.push("create_poll", { type, question, answers })
                .receive("ok", (poll) => {
                    this.setState({ pollsShowForm: false });
                    resolve();
                })
                .receive("error", reject);
        });
    }

    _deletePoll(id) {
        return new Promise((resolve, reject) => {
            this.channel.push("delete_poll", { id })
                .receive("ok", () => {
                    this.setState({ polls: this.state.polls.filter((p) => p.id !== id) });
                    resolve();
                })
                .receive("error", reject);
        });
    }

    _onPollCreated(poll) {
        if (this.state.polls.find((p) => p.id === poll.id) != null) return;
        this.setState({ polls: [poll, ...this.state.polls] });
    }

    _onPollDeleted({ id }) {
        this.setState({ polls: this.state.polls.filter((p) => p.id !== id) });
    }

    // Section: Settings

    _renderSettings() {
        return <>
            <label className="flex items-center space-x-2 cursor-pointer mb-1">
                <input
                    type="checkbox"
                    checked={this.state.readOnly}
                    onChange={(e) => this.setReadOnly(e.target.checked)}
                    className="form-check" />
                <span>Lock chat for viewers</span>
            </label>

            <label className="flex items-center space-x-2 cursor-pointer">
                <input
                    type="checkbox"
                    disabled={this.state.readOnly}
                    checked={this.state.type === "moderated"}
                    onChange={(e) => this.setType(e.target.checked ? 'moderated' : 'simple')}
                    className="form-check" />
                <span>Moderated</span>
            </label>

            {this.state.messages.length > 0 && <div className="flex flex-row space-x-2 mt-2">
                <button className="btn-secondary flex-1" onClick={() => this.clearMessages()}>Clear</button>
                <button className="btn-primary flex-1" disabled={this.state.isDownloading} onClick={this._downloadMessages}>Download</button>
            </div>}
        </>;
    }

    render() {
        if (!this.state.loaded) {
            return (
                <div className="center-vertically floating:invisible">
                    <LoadingSpinner>Chat is loading…</LoadingSpinner>
                </div>
            );
        }

        return (
            <div className="h-screen flex flex-col">
                {this.state.poll && <div className="m-2 mb-0"><PollVoting
                    key={this.state.poll.details.id}
                    onSubmit={this._onPollVote}
                    onToggle={window.setTimeout.bind(window, this.scrollToBottom, 0)}>{this.state.poll}</PollVoting></div>}

                {this.state.messages.length === 0 ? this._renderEmptyState() : <div className="h-auto mt-auto p-3 overflow-y-auto thin-scrollbar" ref={this._messagesRef}>
                    {Array.from(this.state.messages).reverse().reduce(this._renderMessage, []).reverse()}
                </div>}

                {this._renderBottomSheet()}

                <div className="flex-shrink-0 floating:m-3 floating:mt-0 floating:rounded-md floating:overflow-hidden">
                    {this._renderInput()}
                </div>
            </div>
        );
    }
}

const LoadingSpinner = ({ children }) => {
    return <>
        <svg className="animate-spin h-8 w-8 text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <p className="mt-2 text-muted animate-pulse">{children}</p>
    </>;
}

const LoginForm = ({ onLogin }) => {
    const textAreaRef = useRef();
    const [name, setName] = useState("");
    const isInvalid = name.trim().length === 0;

    const onSubmit = (event) => {
        event.preventDefault();
        onLogin(name);
    }

    useEffect(() => {
        textAreaRef.current?.focus({ preventScroll: true });
    }, []);

    return <form className={`flex items-stretch text-sm pl-3 floating:pl-1 transition-colors border-t floating:border-t-0 ${isInvalid ? "bg-gray-200 text-black" : "bg-primary text-on-primary"}`} onSubmit={onSubmit}>
        <TextareaAutosize
            ref={textAreaRef}
            maxLength={50}
            maxRows={1}
            className="py-4 floating:py-2 mx-2 bg-transparent flex-1 resize-none outline-none overflow-y-hidden"
            onKeyDown={(event) => {
                if (event.keyCode === 13) {
                    event.preventDefault();
                    onLogin(name);
                }
            }}
            placeholder="Whats your name?"
            value={name}
            onChange={(event) => setName(event.target.value)} />

        <button
            type="submit"
            disabled={isInvalid}
            className="bg-primary transition-colors rounded-none text-on-primary px-3 disabled:bg-gray-300"><AcceptImg /></button>
    </form>;
}

const ChatForm = function ({ channel, onMessage, handleError, visibleSheet, showSurveySheet, showSettingsSheet }) {
    const textAreaRef = useRef();
    const [message, setMessage] = useState("");
    const [showEmojis, setShowEmojis] = useState(false);
    const isInvalid = useMemo(() => message.trim().length === 0, [message]);

    useEffect(() => {
        textAreaRef.current?.focus({ preventScroll: true });
    }, []);

    const onEmojiClick = (event, emojiObject) => {
        setMessage(message + emojiObject.emoji);
        setShowEmojis(false);
        textAreaRef.current.focus({ preventScroll: true });
    }

    const toggleEmojis = () => {
        setShowEmojis(!showEmojis);
        textAreaRef.current.focus({ preventScroll: true });
    }

    const submitMessage = (event) => {
        if (event) event.preventDefault();
        if (isInvalid) return;

        if (showEmojis) toggleEmojis();

        channel.push("new_msg", { body: message.trim() })
            .receive("ok", (message) => {
                setMessage("");
                onMessage(message);
            })
            .receive("error", handleError)
            .receive("timeout", console.error);
    }

    return <>
        {showEmojis && <Picker
            onEmojiClick={onEmojiClick}
            disableSearchBar={true}
            disableSkinTonePicker={true}
            native={true} />}

        <form className="flex items-stretch text-sm space-x-2 floating:pl-1 bg-white text-black border-t floating:border-t-0" onSubmit={submitMessage}>
            <SmileyImg
                className={`cursor-pointer self-center w-5 h-5 ml-2 floating:ml-1 ${showEmojis ? "text-primary" : "text-gray-500"} floating:hidden`}
                onClick={toggleEmojis} />

            <TextareaAutosize
                ref={textAreaRef}
                className="py-4 floating:py-2 bg-transparent flex-1 resize-none outline-none overflow-y-hidden"
                maxLength={300}
                maxRows={3}
                onKeyDown={(event) => {
                    if (event.keyCode === 13) {
                        event.preventDefault()
                        submitMessage();
                    }
                }}
                placeholder="Type your message"
                value={message}
                onChange={(event) => setMessage(event.target.value)} />

            {showSurveySheet && <SurveyImg
                className={`cursor-pointer self-center w-5 h-5 ${visibleSheet === SHEET_POLL ? "text-primary" : "text-gray-500"}`}
                onClick={showSurveySheet} />}

            {showSettingsSheet && <SettingsImg
                className={`cursor-pointer self-center w-5 h-5 ${visibleSheet === SHEET_SETTINGS ? "text-primary" : "text-gray-500"}`}
                onClick={showSettingsSheet} />}

            <button type="submit" className="bg-primary transition-colors text-on-primary px-3 rounded-none">
                <SendImg />
            </button>
        </form>
    </>;
}
